import { cleanDesignName, uniqueDesignName, generatedDesignName } from '../../utils/designName';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { message, Modal, Form, Input, Upload } from 'antd';
import { DatabaseOutlined, DiffOutlined, DownloadOutlined, EditOutlined, FileExcelOutlined, QuestionCircleOutlined, UploadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useProjectStore } from '../../hooks/useProjectStore';
import { aiAPI, formationAPI, fractureAPI, getApiErrorMessage, sweetSpotAPI, wellboreAPI, type AiDesignAudit } from '../../services/api';
import { cloneMaterial, MATERIAL_PRESETS, materialHandoffKey, materialStorageKey, scopedEngineeringStorageKey, setEngineeringDesignContext } from './fractureEngineering';
import { deduplicateFractureDesigns, fractureDesignDisplayKey } from '../../utils/fractureDesignIdentity';
import { buildScoringTemplate, RESERVOIR_LABELS, scoreFracPlan, type FracEvalLike, type FracStageLike, type ScoringConfig } from '../../utils/fractureScoring';
import { downloadFracturePlanTemplate, parseFracturePlanWorkbook } from '../../utils/fracturePlanExcel';
import { simulationHandoffUrl } from '../fracsim/designSimulationHandoff';
import { useAgentCommands } from '../../services/agent/registry';

// ═══════════════════════════════════════════════
// /fracture/new-design — 建立方案；/fracture/designs — 方案总览
// ═══════════════════════════════════════════════
const TH = {
  bg: '#FFFFFF', text: '#1E2A3A', textSec: '#4F6680', textMuted: '#8598AD',
  accent: '#C2533A', accentLight: 'rgba(194,83,58,0.08)',
  headerBorder: '#D6DAE0', panelHeaderBg: '#F4F6F9',
  ok: '#1E8449', warn: '#D4770C', stage: '#2874A6',
  card: '#F9FAFB', cardActive: '#FFF5F1',
  fontMono: '"Cascadia Code",Consolas,"SF Mono","Microsoft YaHei",monospace',
};

type Design = {
  id: number; name: string; is_active: boolean;
  stage_count: number; cluster_per_stage: number; cluster_spacing?: number; stage_spacing: number;
  pump_rate: number; total_fluid: number; total_proppant: number;
  fluid_type: string; proppant_type: string; max_proppant_conc: number;
  notes: string; created_at: string; updated_at?: string; stages?: FracStageLike[];
  generation_status?: 'created' | 'existing';
  design_data?: {
    design_fingerprint?: string;
    stage_cluster_design?: Array<{
      risk_index?: number;
      competition?: { uniformity?: number };
    }>;
    optimization_run?: {
      reservoir_model?: {
        model_name?: string;
        model_version?: string;
        is_fallback?: boolean;
        data_confidence?: { level?: string; score?: number | null };
      };
      version_record?: {
        algorithm_version?: string;
        calibration_status?: string;
      };
    };
    completion_design?: {
      parameters?: Record<string, number>;
      stages?: Array<{
        stageNumber: number; topMd: number; bottomMd: number;
        completion: { mode: string; casingType: string; casingInnerDiameter: number; isolationMethod: string; pressureRating: number };
        clusters: Array<{ topMd: number; bottomMd: number; effectiveHoles: number }>;
      }>;
      ai?: { source?: string; model?: string | null };
    };
    materials_design?: { source?: string; fluid_system?: string; proppant_system?: string; maximum_proppant_concentration?: number; selection_basis?: string };
    pump_design?: { source?: string; stage_count?: number; step_count?: number; design_rate?: number; total_fluid?: number; total_proppant?: number };
  };
};

type AnalogCandidate = {
  design_id: number;
  project_id: number;
  well_name: string;
  target_formation: string;
  design_name: string;
  stage_count: number;
  validation_status: string;
  verified: boolean;
  similarity_score: number;
  data_completeness: number;
  distance_km: number | null;
  recommendation: string;
};

type TrajectoryStation = {
  md: number;
  tvd: number;
  ns: number | null;
  ew: number | null;
};

type FormationLayer = {
  name: string;
  top: number;
  bottom: number;
  color: string;
};

const SCORING_CONFIG_KEY = 'fracture_scoring_config_v2';
const formatDesignDate = (value?: string) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false }).format(date);
};
const loadScoringConfig = (): ScoringConfig => {
  const fallback: ScoringConfig = { reservoirType: 'shale', intent: 'balanced' };
  try {
    const raw = localStorage.getItem(SCORING_CONFIG_KEY);
    return raw ? { ...fallback, ...JSON.parse(raw) } : fallback;
  } catch {
    return fallback;
  }
};

const designInsight = (design: Design, score: ReturnType<typeof scoreFracPlan>) => {
  const stageRows = design.design_data?.stage_cluster_design || [];
  const highRiskStages = stageRows.filter((stage) => Number(stage.risk_index || 0) >= 0.6).length;
  const unevenStages = stageRows.filter((stage) => {
    const uniformity = stage.competition?.uniformity;
    return uniformity !== undefined && uniformity < 0.65;
  }).length;

  const issueCount = (issue: string) => score.checks.filter((check) => check.issues.includes(issue)).length;
  const risks = [
    highRiskStages ? `${highRiskStages}段风险指数偏高` : '',
    unevenStages ? `${unevenStages}段多簇进液不均` : '',
    issueCount('簇间距低于工程下限') ? `${issueCount('簇间距低于工程下限')}段簇距低于工程下限` : '',
    issueCount('单米液量超过工程警戒值') ? `${issueCount('单米液量超过工程警戒值')}段液量强度偏高` : '',
    issueCount('单米砂量超过工程警戒值') ? `${issueCount('单米砂量超过工程警戒值')}段砂量强度偏高` : '',
    issueCount('段内包含 IV 类甜点') ? `${issueCount('段内包含 IV 类甜点')}段包含IV类甜点` : '',
  ].filter(Boolean);
  if (risks.length) return `风险：${risks.slice(0, 2).join('；')}`;

  const optimizationPoints = [
    issueCount('段长高于工程推荐范围') || issueCount('段长低于工程推荐范围') ? '调整段长与分段边界' : '',
    issueCount('簇数高于工程推荐范围') || issueCount('簇数低于工程推荐范围') ? '优化每段簇数' : '',
    issueCount('簇间距高于工程推荐范围') || issueCount('簇间距低于工程推荐范围') ? '优化簇间距' : '',
    issueCount('甜点等级变化较大') || issueCount('甜点等级略有变化') ? '提高段内甜点一致性' : '',
    issueCount('簇群长度占段长比例偏高') ? '增加端部施工余量' : '',
    issueCount('单米液量低于工程推荐范围') || issueCount('单米液量高于工程推荐范围') ? '优化液量强度' : '',
    issueCount('单米砂量低于工程推荐范围') || issueCount('单米砂量高于工程推荐范围') ? '优化加砂强度' : '',
  ].filter(Boolean);
  if (optimizationPoints.length) return `提高建议：${optimizationPoints.slice(0, 2).join('；')}`;

  const lowest = [
    { score: score.breakdown.stageLength, text: '复核段界与目标段长' },
    { score: score.breakdown.clusterSpacing, text: '复核簇数与簇距匹配' },
    { score: score.breakdown.sweetSpot, text: '提高甜点覆盖一致性' },
    { score: score.breakdown.fluidSand, text: '校核液砂规模' },
  ].sort((a, b) => a.score - b.score)[0];
  return lowest.score < 96
    ? `提高建议：${lowest.text}`
    : '推荐条件：开展裂缝模拟，验证缝长与进液均匀性';
};

const scoreGuidance = (score: ReturnType<typeof scoreFracPlan> | null) => {
  if (!score || !score.checks.length) return '潜在风险：缺少分段或地质品质数据，暂不能形成自适应评价';
  if (score.score >= 82) return `推荐条件：总体评分 ${score.score}，段级取向与当前地质品质匹配，可进入裂缝模拟验证`;
  if (score.score >= 65) {
    const advice = score.checks.flatMap(check => check.suggestions).filter(Boolean).slice(0, 2).join('；');
    return `提高建议：${advice || '优先调整最低分项并复核段界、簇距及液砂强度'}`;
  }
  return `潜在风险：${score.summary.slice(0, 2).join('；') || '段内地质品质与当前工程参数匹配度偏低'}`;
};

export default function FractureDesignsPage({ mode = 'overview' }: { mode?: 'create' | 'overview' }) {
  const { currentProject } = useProjectStore();
  const projectId = currentProject?.id;
  const nav = useNavigate();

  const [designs, setDesigns] = useState<Design[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const saveGeneratedName = async (id: number, type: string) => {
    const name = uniqueDesignName(generatedDesignName(currentProject?.well_name || '当前井', type), designs.filter(d => d.id !== id).map(d => d.name));
    await fractureAPI.renameDesign(id, name);
  };
  const [brief, setBrief] = useState('');
  const [aiAudit, setAiAudit] = useState<AiDesignAudit | null>(null);
  const [newDesignOpen, setNewDesignOpen] = useState(false);
  const [newDesignName, setNewDesignName] = useState('新建方案');
  const [newDesignBusy, setNewDesignBusy] = useState(false);
  const [analogOpen, setAnalogOpen] = useState(false);
  const [analogLoading, setAnalogLoading] = useState(false);
  const [analogCandidates, setAnalogCandidates] = useState<AnalogCandidate[]>([]);
  const [selectedAnalogId, setSelectedAnalogId] = useState<number | null>(null);
  const [compareOpen, setCompareOpen] = useState(false);
  const [compareIds, setCompareIds] = useState<number[]>([]);
  const [renameTarget, setRenameTarget] = useState<Design | null>(null);
  const [renameName, setRenameName] = useState('');
  const [renameBusy, setRenameBusy] = useState(false);
  const [selectedDesignId, setSelectedDesignId] = useState<number | null>(null);
  const [selectedDetail, setSelectedDetail] = useState<Design | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const detailRequestRef = useRef(0);
  const listRequestRef = useRef(0);
  const [trajectory, setTrajectory] = useState<TrajectoryStation[]>([]);
  const [formationLayers, setFormationLayers] = useState<FormationLayer[]>([]);
  const [sweetEvals, setSweetEvals] = useState<FracEvalLike[]>([]);
  const [scoringConfig] = useState<ScoringConfig>(() => loadScoringConfig());
  const [scoreHelp, setScoreHelp] = useState<'plan' | 'stage' | null>(null);
  const scoringTemplate = useMemo(() => buildScoringTemplate(scoringConfig), [scoringConfig]);
  const selectedPlanScore = useMemo(() => selectedDetail ? scoreFracPlan(selectedDetail, sweetEvals, scoringConfig) : null, [selectedDetail, sweetEvals, scoringConfig]);
  const activeScoringTemplate = selectedPlanScore?.template ?? scoringTemplate;
  const visibleDesigns = useMemo(() => deduplicateFractureDesigns(designs), [designs]);
  const comparedDesigns = visibleDesigns.filter((design) => compareIds.includes(design.id));

  const load = async () => {
    if (!projectId) return;
    const requestId = ++listRequestRef.current;
    setLoading(true);
    try {
      const { data } = await fractureAPI.listDesigns(projectId);
      if (requestId === listRequestRef.current) setDesigns(Array.isArray(data) ? data : (data?.results ?? []));
    } catch (e: any) {
      if (requestId === listRequestRef.current) message.error(e?.message || '加载失败');
    } finally {
      if (requestId === listRequestRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    detailRequestRef.current += 1;
    listRequestRef.current += 1;
    setSelectedDesignId(null);
    setSelectedDetail(null);
    setDetailLoading(false);
    setDesigns([]);
    load();
  }, [projectId]);

  useEffect(() => {
    if (!projectId) {
      setTrajectory([]);
      setFormationLayers([]);
      setSweetEvals([]);
      return;
    }
    let cancelled = false;
    const unwrap = (data: any) => Array.isArray(data) ? data : data?.results ?? [];
    Promise.allSettled([
      wellboreAPI.getSurvey(projectId),
      formationAPI.getLayers(projectId),
      sweetSpotAPI.list(projectId),
    ]).then(([surveyResult, layerResult, sweetResult]) => {
      if (cancelled) return;
      if (surveyResult.status === 'fulfilled') {
        setTrajectory(unwrap(surveyResult.value.data).map((row: any) => ({
          md: Number(row.md ?? 0),
          tvd: Number(row.tvd ?? row.md ?? 0),
          ns: row.ns == null ? null : Number(row.ns),
          ew: row.ew == null ? null : Number(row.ew),
        })).filter((row: TrajectoryStation) => Number.isFinite(row.md) && Number.isFinite(row.tvd)).sort((a: TrajectoryStation, b: TrajectoryStation) => a.md - b.md));
      } else setTrajectory([]);
      if (layerResult.status === 'fulfilled') {
        setFormationLayers(unwrap(layerResult.value.data).map((row: any) => ({
          name: String(row.name ?? '未命名层'),
          top: Number(row.top_depth ?? 0),
          bottom: Number(row.bottom_depth ?? row.top_depth ?? 0),
          color: String(row.color_code ?? '#78909C'),
        })).filter((row: FormationLayer) => Number.isFinite(row.top) && Number.isFinite(row.bottom)).sort((a: FormationLayer, b: FormationLayer) => a.top - b.top));
      } else setFormationLayers([]);
      if (sweetResult.status === 'fulfilled') {
        setSweetEvals(unwrap(sweetResult.value.data).map((row: any) => ({ top: Number(row.top_depth ?? 0), bottom: Number(row.bottom_depth ?? row.top_depth ?? 0), grade: String(row.grade ?? 'III'), score: Number(row.composite_score ?? 0.5) })).filter((row: FracEvalLike) => Number.isFinite(row.top) && Number.isFinite(row.bottom)).sort((a: FracEvalLike, b: FracEvalLike) => a.top - b.top));
      } else setSweetEvals([]);
    });
    return () => { cancelled = true; };
  }, [projectId]);

  const selectDesign = async (design: Design) => {
    const requestId = ++detailRequestRef.current;
    setSelectedDesignId(design.id);
    setSelectedDetail(null);
    setDetailLoading(true);
    try {
      const {data}=await fractureAPI.getDesign(design.id);
      if (requestId === detailRequestRef.current) setSelectedDetail(data);
    } catch {
      if (requestId === detailRequestRef.current) {
        setSelectedDetail(design);
        message.warning('完整方案明细读取失败，当前显示列表摘要');
      }
    } finally {
      if (requestId === detailRequestRef.current) setDetailLoading(false);
    }
  };

  const autoDesign = async (mode: 'legacy' | 'data_driven' = 'legacy') => {
    if (!projectId) return;
    setBusy(true);
    try {
      const { data } = await fractureAPI.autoDesign({ project_id: projectId, options: { mode } });
      if (data.generation_status !== 'existing') await saveGeneratedName(data.id, '类比压裂');
      setAiOpen(false);
      if (data.generation_status === 'existing') {
        message.info('该方案已存在，已切换到已有方案');
      } else {
        message.success(mode === 'data_driven' ? '已生成地层数据驱动方案' : '已按甜点自动生成方案');
      }
      await load();
      askDesignAI(undefined, data.id);
    } catch (e: any) {
      const d = e?.response?.data;
      message.error(typeof d === 'string' ? d : d?.detail || '自动设计失败');
    } finally { setBusy(false); }
  };

  const activate = async (d: Design) => {
    try {
      const previousActiveId = designs.find((design) => design.is_active)?.id;
      await fractureAPI.activateDesign(d.id);
      if (projectId) setEngineeringDesignContext(projectId, d.id, previousActiveId);
      message.success(`已激活 ${d.name}`);
      await load();
      return true;
    } catch { message.error('激活失败'); return false; }
  };

  const openRename = (design: Design) => {
    setRenameTarget(design);
    setRenameName(cleanDesignName(design.name));
  };

  const submitRename = async () => {
    if (!renameTarget) return;
    const name = cleanDesignName(renameName);
    if (!name) {
      message.warning('方案名称不能为空');
      return;
    }
    const duplicate = designs.some(
      (design) => design.id !== renameTarget.id && cleanDesignName(design.name).toLocaleLowerCase() === name.toLocaleLowerCase(),
    );
    if (duplicate) {
      message.warning('当前项目中已存在同名方案');
      return;
    }
    setRenameBusy(true);
    try {
      await fractureAPI.renameDesign(renameTarget.id, name);
      setRenameTarget(null);
      message.success('方案名称已更新');
      await load();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '重命名失败');
    } finally {
      setRenameBusy(false);
    }
  };

  const remove = (d: Design) => {
    const group = designs.filter((item) => fractureDesignDisplayKey(item) === fractureDesignDisplayKey(d));
    Modal.confirm({
      title: `删除方案 "${d.name}"?`,
      content: group.length > 1
        ? `该方案及 ${group.length - 1} 条相同历史记录会一起删除，关联段表、模拟和监测会话也会删除。`
        : '该方案下的所有段、模拟、监测会话都会一起删除。',
      okText: '确认删除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: async () => {
        try {
          await fractureAPI.bulkDeleteDesigns(group.map((item) => item.id));
          message.success(group.length > 1 ? `已删除该方案及 ${group.length - 1} 条相同记录` : '已删除');
          await load();
        } catch (e: any) {
          message.error(e?.response?.data?.detail || '删除失败');
          throw e;
        }
      },
    });
  };

  const openAnalogDesign = async () => {
    if (!projectId) return;
    setAnalogOpen(true);
    setAnalogLoading(true);
    setSelectedAnalogId(null);
    try {
      const { data } = await fractureAPI.listAnalogCandidates(projectId);
      setAnalogCandidates(Array.isArray(data) ? data : []);
    } catch (e: any) {
      setAnalogCandidates([]);
      message.error(e?.response?.data?.detail || '邻井择选方案读取失败');
    } finally {
      setAnalogLoading(false);
    }
  };

  const runAnalogDesign = async () => {
    if (!projectId || !selectedAnalogId) return;
    setBusy(true);
    try {
      const candidate = analogCandidates.find(item => item.design_id === selectedAnalogId);
      const { data } = await fractureAPI.createAnalogDesign({
        project_id: projectId,
        source_design_id: selectedAnalogId,
        allow_unvalidated: !candidate?.verified,
      });
      setAnalogOpen(false);
      await load();
      message[data.generation_status === 'existing' ? 'info' : 'success'](
        data.generation_status === 'existing' ? '该方案已存在' : '邻井择选方案已生成',
      );
      askDesignAI(selectedAnalogId, data.id);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '邻井择选方案生成失败');
    } finally {
      setBusy(false);
    }
  };

  const askDesignAI = (neighborDesignId?: number, designId = selectedDesignId ?? undefined, autoSend = false) => window.dispatchEvent(new CustomEvent('fracsight-ai-open', {
    detail: {
      prompt: neighborDesignId ? '请分析当前选中的邻井与目标井的相似性、参数转换和使用风险。' : '请互动分析当前完整压裂方案，解释分段分簇、完井射孔、材料选择、泵注程序的设计依据和需要人工复核的风险。',
      designId,
      neighborDesignId,
      autoSend,
      projectId,
    },
  }));

  const openComparison = () => {
    const active = visibleDesigns.find((design) => design.is_active) || visibleDesigns[0];
    setCompareIds(active ? [active.id] : []);
    setCompareOpen(true);
  };

  const toggleComparison = (id: number, checked: boolean) => {
    if (!checked) {
      setCompareIds((current) => current.filter((value) => value !== id));
      return;
    }
    setCompareIds((current) => [...current, id]);
  };

  // 导出统一为 Word。文档在后端用 python-docx 成型 —— 表格多、还要带上 AI 留痕,
  // 前端拼不出来, 也不该把"哪些数字是模型给的"散到两处实现。
  // ↓ AI 生成方案用的状态
  const exportDocx = async (d: Design) => {
    try {
      const res = await fractureAPI.exportDesignDocx(d.id);
      const disposition = String(res.headers?.['content-disposition'] ?? '');
      const starMatch = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
      const filename = starMatch ? decodeURIComponent(starMatch[1]) : `${d.name}_压裂方案.docx`;
      const url = URL.createObjectURL(new Blob([res.data as BlobPart]));
      const a = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      // blob 响应里的错误体要先读成文本才看得到 detail
      let detail = '导出失败';
      try {
        const text = await (e?.response?.data as Blob)?.text?.();
        if (text) detail = JSON.parse(text).detail || detail;
      } catch { /* 拿不到就用默认文案 */ }
      message.error(detail);
    }
  };

  // AI 辅助设计: 模型只定取向与区间, 逐段数值仍由后端模板引擎算。
  const hydrateIntegratedDesign = async (designId: number) => {
    if (!projectId) return null;
    const { data: detail } = await fractureAPI.getDesign(designId) as { data: Design };
    const stageRows = (detail.stages ?? []) as any[];
    let completion = detail.design_data?.completion_design;
    if (!completion?.stages?.length && stageRows.length) {
      const { data } = await aiAPI.completionDesign({
        project_id: projectId,
        stages: stageRows.map((stage, index) => ({
          stage_number: Number(stage.stage_number ?? index + 1),
          top_md: Number(stage.top_perf ?? stage.top ?? 0),
          bottom_md: Number(stage.bottom_perf ?? stage.bottom ?? 0),
          cluster_count: Number(detail.cluster_per_stage || 6),
        })),
      });
      completion = data;
    }
    if (completion?.stages?.length) {
      const completionDraft = {
        parameters: completion.parameters,
        stages: completion.stages.map((stage, stageIndex) => ({
          id: `ai-stage-${designId}-${stage.stageNumber}`,
          stageNumber: stage.stageNumber,
          topMd: stage.topMd,
          bottomMd: stage.bottomMd,
          surfaceTemperature: 20,
          pumpStepCount: (stageRows[stageIndex]?.schedule ?? []).length,
          completion: stage.completion,
          clusters: stage.clusters.map((cluster, clusterIndex) => ({ ...cluster, id: `ai-cluster-${designId}-${stage.stageNumber}-${clusterIndex}` })),
        })),
      };
      localStorage.setItem(scopedEngineeringStorageKey('fracture_perforation_stages_v1', projectId), JSON.stringify(completionDraft));
    }
    const materialMeta = detail.design_data?.materials_design;
    const preset = MATERIAL_PRESETS.find((item) => String(materialMeta?.fluid_system || detail.fluid_type).includes(item.fluidSystem))
      ?? (String(materialMeta?.fluid_system || detail.fluid_type).includes('线性胶') ? MATERIAL_PRESETS[2] : MATERIAL_PRESETS[1]);
    const material = {
      ...cloneMaterial(preset),
      name: `${detail.name}材料方案`,
      fluidSystem: materialMeta?.fluid_system || detail.fluid_type || preset.fluidSystem,
      designBasis: materialMeta?.selection_basis || preset.designBasis,
    };
    localStorage.setItem(materialStorageKey(projectId), JSON.stringify(material));
    localStorage.setItem(materialHandoffKey(projectId), JSON.stringify({ ...material, handedOffAt: new Date().toISOString() }));
    const generatedMaterial = materialMeta ?? {
      source: 'ai_integrated_design',
      fluid_system: material.fluidSystem,
      proppant_system: detail.proppant_type || material.proppants.map((item) => item.name).join('、'),
      maximum_proppant_concentration: detail.max_proppant_conc,
      selection_basis: material.designBasis,
    };
    const generatedPump = detail.design_data?.pump_design ?? {
      source: 'ai_integrated_design',
      stage_count: stageRows.length,
      step_count: stageRows.reduce((sum, stage) => sum + (Array.isArray(stage.schedule) ? stage.schedule.length : 0), 0),
      design_rate: detail.pump_rate,
      total_fluid: detail.total_fluid,
      total_proppant: detail.total_proppant,
    };
    await fractureAPI.saveSimulationHandoff({
      project_id: projectId,
      design_id: designId,
      ...(completion ? { completion: completion as unknown as Record<string, unknown> } : {}),
      materials: material as unknown as Record<string, unknown>,
    }).catch(() => undefined);
    const integratedDetail: Design = {
      ...detail,
      design_data: {
        ...(detail.design_data ?? {}),
        ...(completion ? { completion_design: completion } : {}),
        materials_design: generatedMaterial,
        pump_design: generatedPump,
      },
    };
    setSelectedDesignId(designId);
    setSelectedDetail(integratedDetail);
    return integratedDetail;
  };

  const aiDesign = async () => {
    if (!projectId) return;
    const previousActiveId = designs.find((design) => design.is_active)?.id;
    setBusy(true);
    try {
      const { data } = await fractureAPI.aiDesign({
        project_id: projectId,
        brief: brief.trim() || undefined,
      });
      await saveGeneratedName(data.id, '智能压裂');
      setEngineeringDesignContext(projectId, data.id, previousActiveId);
      await hydrateIntegratedDesign(data.id);
      setAiOpen(false);
      setAiAudit(data.ai);
      message.success(data.ai?.source === 'llm' ? 'AI 已生成完整工程方案' : '已生成完整工程方案（本次大模型不可用，采用规则引擎）');
      await load();
      askDesignAI(undefined, data.id, true);
    } catch (e: any) {
      message.error(getApiErrorMessage(e, 'AI 设计失败，请稍后重试'));
    } finally { setBusy(false); }
  };

  const handleImport = async (file: File) => {
    if (!projectId) return false;
    try {
      const payload = parseFracturePlanWorkbook(await file.arrayBuffer(), file.name);
      const baseName = cleanDesignName(String(payload.design.name || 'Excel导入方案')) || 'Excel导入方案';
      payload.design.name = uniqueDesignName(baseName, designs.map(design => design.name));
      const body = { project_id: projectId, design: payload.design, stages: payload.stages };
      const { data } = await fractureAPI.importPlan(body);
      if (data.generation_status === 'existing') {
        message.info('该方案已存在，已切换到已有方案');
      } else {
        message.success(`Excel 导入成功，共识别 ${payload.stages.length} 个压裂段`);
      }
      if (payload.warnings.length) message.warning(payload.warnings.join('；'));
      setImportOpen(false);
      await load();
    } catch (e: any) {
      const d = e?.response?.data;
      message.error(typeof d === 'string' ? d : d?.detail || e?.message || 'Excel 导入失败');
    }
    return false; // 阻止 antd 自动上传
  };

  const createManualDesign = async () => {
    if (!projectId) return;
    const baseName = cleanDesignName(newDesignName) || '新建方案';
    if (!baseName) return void message.warning('请输入方案名称');
    const finalName = uniqueDesignName(baseName, designs.map(design => design.name));
    const previousActiveId = designs.find(design => design.is_active)?.id;
    setNewDesignBusy(true);
    try {
      const { data } = await fractureAPI.createDesign({
        project: projectId,
        name: finalName,
        stage_count: 0,
        cluster_per_stage: 0,
        cluster_spacing: 0,
        stage_spacing: 0,
        pump_rate: 0,
        total_fluid: 0,
        total_proppant: 0,
        fluid_type: '未设计',
        proppant_type: '未设计',
        max_proppant_conc: 0,
        notes: '[DRAFT] 尚未进行工程设计',
        is_active: false,
      });
      await fractureAPI.activateDesign(data.id);
      setEngineeringDesignContext(projectId, data.id, previousActiveId);
      setNewDesignOpen(false);
      await load();
      message.success(finalName === baseName ? `已新建方案“${finalName}”` : `名称重复，已新建为“${finalName}”`);
      nav('/fracture/builder');
    } catch (error: any) {
      message.error(error?.response?.data?.detail || '新建方案失败');
    } finally {
      setNewDesignBusy(false);
    }
  };

  const enterEngineering = async (design: Design) => {
    if (!projectId) return;
    const previousActiveId = designs.find(item => item.is_active)?.id;
    try {
      if (!design.is_active) await fractureAPI.activateDesign(design.id);
      setEngineeringDesignContext(projectId, design.id, previousActiveId);
      nav(`/fracture/builder?design=${design.id}`);
    } catch (error: any) {
      message.error(error?.response?.data?.detail || '进入工程设计失败');
    }
  };

  // 爱压 AI 操作模式: 复用本页按钮逻辑
  useAgentCommands({
    'design.list': () => (designs.length
      ? designs.map((d) => `#${d.id} ${d.name}${d.is_active ? '（当前）' : ''} · ${d.stage_count} 段`).join('\n')
      : '当前项目还没有压裂方案'),
    // 只打开"AI 生成方案"对话框并预填补充要求, 生成由用户在对话框里确认
    'design.ai_generate': (args) => {
      if (busy) throw new Error('页面正忙, 请稍后再试');
      setBrief(typeof args.brief === 'string' ? args.brief : '');
      setAiOpen(true);
      return '已打开「AI 生成方案」对话框, 请核对补充要求后点击确定';
    },
    'design.activate': async (args) => {
      const target = designs.find((d) => d.id === Number(args.design_id));
      if (!target) throw new Error(`没有编号为 ${args.design_id} 的方案, 可先执行 /方案列表`);
      if (!(await activate(target))) throw new Error('激活失败');
      return `已激活 #${target.id} ${target.name}`;
    },
  }, () => `方案总览: 共 ${designs.length} 个方案; 当前方案: ${designs.find((d) => d.is_active)?.name ?? '无'}`);

  if (!projectId) return <div style={{ padding: 40, color: TH.textSec }}>请先选择项目</div>;

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: TH.bg, fontFamily: 'inherit' }}>
      {mode === 'create' ? <>
        <div style={{ padding: '10px 20px', borderBottom: `1px solid ${TH.headerBorder}` }}>
          <h2 style={{ fontSize: 15, fontWeight: 700, color: TH.text, margin: 0 }}>新建方案</h2>
        </div>
        <main style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: 16, background: '#EEF2F6' }}>
          <section style={{ width: '100%', minHeight: '100%', display: 'grid', gridTemplateColumns: 'minmax(380px,.9fr) minmax(580px,1.35fr)', gap: 16, alignItems: 'stretch' }}>
            <div style={createPanel}>
              <div style={createPanelHeader}><b>方案新建方式</b></div>
              <div style={{ minHeight: 540, padding: 14, display: 'grid', gridTemplateRows: 'repeat(3,minmax(150px,1fr))', gap: 12 }}>
                <button type="button" onClick={() => setImportOpen(true)} disabled={busy} style={{...creationCard,minHeight:0}}>
                  <UploadOutlined style={{ fontSize: 22, color: TH.stage }} />
                  <b>人工新建或导入</b><span>新建空白方案进入工程设计，或导入 Excel 通用方案</span>
                </button>
                <button type="button" onClick={openAnalogDesign} disabled={busy} style={{...creationCard,minHeight:0}}>
                  <DatabaseOutlined style={{ fontSize: 22, color: '#1F6F68' }} />
                  <b>邻井择选</b><span>从相似邻井候选方案中选择基准，并映射到当前井</span>
                </button>
                <button type="button" onClick={() => { setBrief(''); setAiOpen(true); }} disabled={busy} style={{...creationCard,minHeight:0}}>
                  <span style={{ fontSize: 22, color: TH.accent }}>✦</span>
                  <b>AI 生成方案</b><span>结合地层、甜点和力学条件生成完整工程方案</span>
                </button>
              </div>
            </div>
            <div style={createPanel}>
              <div style={createPanelHeader}><b>已建立方案</b><span>{visibleDesigns.length} 个方案</span></div>
              <div style={{ margin: 14, border: `1px solid ${TH.headerBorder}`, background: '#fff' }}>
                <div style={{...designEntry,minHeight:38,cursor:'default',background:TH.panelHeaderBg,fontWeight:700,color:TH.textSec}}><span>方案名称</span><span>修改日期</span><span>状态</span><span>操作</span></div>
                {loading ? <div style={{ padding: 28, textAlign: 'center', color: TH.textMuted }}>加载中…</div> : visibleDesigns.length ? visibleDesigns.map(design => (
                  <button key={design.id} type="button" onClick={() => void enterEngineering(design)} style={{...designEntry,minHeight:58,background:design.is_active?'#FFF6F1':'#fff',boxShadow:design.is_active?`inset 3px 0 ${TH.accent}`:undefined}}>
                    <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 700 }}>{cleanDesignName(design.name)}</span>
                    <span style={{ color: TH.textMuted }}>{formatDesignDate(design.updated_at || design.created_at)}</span>
                    <span style={{ color: design.is_active?TH.accent:TH.textMuted, fontWeight: 700 }}>{design.is_active ? '当前方案' : '已保存'}</span>
                    <span style={{ color: TH.stage, fontWeight: 700 }}>进入工程设计 →</span>
                  </button>
                )) : <div style={{ padding: 32, textAlign: 'center', color: TH.textMuted }}>暂无方案，请从左侧选择一种方式建立方案</div>}
              </div>
            </div>
          </section>
        </main>
      </> : <>
        <div style={{ padding: '8px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${TH.headerBorder}`, gap: 8 }}>
          <h2 style={{ fontSize: 15, fontWeight: 700, color: TH.text, margin: 0 }}>方案总览</h2>
          <button onClick={openComparison} disabled={!visibleDesigns.length} style={{ ...btnSecondary, color: TH.textSec }}>
            <DiffOutlined /> 方案对比
          </button>
        </div>

        <div style={{flex:1,minHeight:0,display:'grid',gridTemplateColumns:'260px minmax(0,1fr)',background:'#EEF1F4',gap:1}}>
        <aside style={{background:'#fff',minHeight:0,overflow:'auto',borderRight:`1px solid ${TH.headerBorder}`}}>
          <div style={{height:30,display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:700,color:TH.textSec,background:TH.panelHeaderBg,borderBottom:`1px solid ${TH.headerBorder}`}}>名称</div>
          {loading?<div style={{padding:20,textAlign:'center',color:TH.textMuted}}>加载中…</div>:visibleDesigns.length?visibleDesigns.map(d=>(
            <button key={d.id} type="button" onClick={()=>void selectDesign(d)} style={{width:'100%',minHeight:34,padding:'5px 10px',display:'grid',gridTemplateColumns:'minmax(0,1fr)',alignItems:'center',gap:6,border:'none',borderBottom:`1px solid ${TH.headerBorder}`,background:selectedDesignId===d.id?'#EAF2F8':'#fff',color:selectedDesignId===d.id?TH.stage:TH.text,cursor:'pointer',fontSize:12,textAlign:'left'}}>
              <span style={{overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{cleanDesignName(d.name)}</span>
            </button>
          )):<div style={{padding:24,textAlign:'center',color:TH.textMuted,fontSize:12}}>暂无方案</div>}
        </aside>
        <main style={{minWidth:0,minHeight:0,overflow:'auto',background:'#fff'}}>
          {!selectedDesignId?<div style={{height:'100%',background:'#fff'}}/>:detailLoading?<div style={{padding:40,textAlign:'center',color:TH.textMuted}}>正在读取方案缩略图…</div>:selectedDetail&&(()=>{
            const d=selectedDetail,score=selectedPlanScore??scoreFracPlan(d,sweetEvals,scoringConfig),stageRows=(d.stages??[]) as any[];
            return <div style={{padding:16}}>
              <div style={{display:'flex',alignItems:'center',gap:9,paddingBottom:10,borderBottom:`1px solid ${TH.headerBorder}`}}>
                <b style={{fontSize:14}}>压裂设计缩略图 · {cleanDesignName(d.name)}</b>
                {d.is_active&&<span style={{fontSize:10,padding:'2px 7px',borderRadius:8,background:TH.accent,color:'#fff'}}>当前方案</span>}
                <span style={{fontSize:11,color:score.color,fontWeight:800}}>{score.label} {score.score} 分</span>
                <div style={{marginLeft:'auto',display:'flex',gap:5}}>
                  {!d.is_active&&<button onClick={()=>activate(d)} style={btnCardPrimary}>设为当前方案</button>}
                  <button onClick={()=>nav(simulationHandoffUrl(d.id))} style={btnCardPrimary}>进入压裂模拟</button>
                  <button onClick={()=>openRename(d)} style={btnCard}><EditOutlined/> 重命名</button>
                  <button onClick={()=>exportDocx(d)} style={btnCard}>导出 Word</button>
                  <button onClick={()=>remove(d)} style={{...btnCard,color:TH.warn}}>删除</button>
                </div>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(7,minmax(90px,1fr))',gap:1,background:TH.headerBorder,marginTop:12,border:`1px solid ${TH.headerBorder}`}}>
                {[
                  ['段数',d.stage_count,'段'],['平均段长',d.stage_spacing,'m'],['平均簇数',d.cluster_per_stage,'簇'],['平均簇距',d.cluster_spacing??'—','m'],['排量',d.pump_rate,'m³/min'],['总液量',d.total_fluid,'m³'],['总砂量',d.total_proppant,'t'],
                ].map(([label,value,unit])=><div key={String(label)} style={{padding:'9px 8px',background:'#fff',textAlign:'center'}}><div style={{fontSize:10,color:TH.textMuted}}>{label}</div><b style={{fontFamily:TH.fontMono,fontSize:14}}>{value}</b><small style={{marginLeft:3,color:TH.textMuted}}>{unit}</small></div>)}
              </div>
              <div style={{display:'grid',gridTemplateColumns:'minmax(150px,1.2fr) repeat(4,1fr)',gap:1,background:TH.headerBorder,borderTop:`1px solid ${TH.headerBorder}`}}>
                <div style={{background:'#fff',padding:'6px 10px',display:'flex',alignItems:'center',justifyContent:'space-between',gap:6}}><span style={{fontSize:10,color:TH.textMuted,display:'inline-flex',alignItems:'center',gap:4}}>方案评分<button type="button" aria-label="查看方案评分公式" title="查看评分公式与参数" onClick={()=>setScoreHelp('plan')} style={helpButton}><QuestionCircleOutlined/></button></span><span style={{fontSize:12,fontWeight:800,color:score.color}}>{score.label} {score.score} 分</span></div>
                {[
                  ['段长合理性',score.breakdown.stageLength],['簇数/簇间距',score.breakdown.clusterSpacing],['甜点一致性',score.breakdown.sweetSpot],['液砂匹配',score.breakdown.fluidSand],
                ].map(([label,value])=><div key={String(label)} style={{background:'#fff',padding:'6px 8px',display:'flex',alignItems:'center',justifyContent:'space-between',gap:6}}><span style={{fontSize:10,color:TH.textMuted}}>{label}</span><b style={{fontSize:12,color:typeof value==='number'?(value>=82?TH.ok:value>=65?TH.warn:'#C0392B'):TH.textMuted}}>{value}</b></div>)}
              </div>
              <div style={{display:'grid',gridTemplateColumns:'minmax(0,1fr)',gap:1,background:TH.headerBorder,marginTop:1,minHeight:500}}>
                <section style={{background:'#fff',minWidth:0,display:'flex',flexDirection:'column'}}>
                  <div style={{padding:'6px 10px',background:TH.panelHeaderBg,borderBottom:`1px solid ${TH.headerBorder}`,fontSize:11,fontWeight:700,color:TH.stage}}>分段参数 ({stageRows.length} 段) · {d.fluid_type||'—'} / {d.proppant_type||'—'}</div>
                  {stageRows.length?<div style={{overflow:'auto',flex:1}}><table style={{width:'100%',minWidth:920,borderCollapse:'collapse',fontSize:11}}><thead><tr style={{background:TH.panelHeaderBg,position:'sticky',top:0}}>{['段','射孔顶深','射孔底深','段长','簇数','平均簇距','压裂段工程评分','簇位缩略'].map(x=><th key={x} style={{padding:'6px 8px',textAlign:'left',borderBottom:`1px solid ${TH.headerBorder}`,color:TH.textSec,whiteSpace:'nowrap'}}>{x==='压裂段工程评分'?<span style={{display:'inline-flex',alignItems:'center',gap:4}}>{x}<button type="button" aria-label="查看压裂段工程评分公式" title="查看评分公式与参数" onClick={()=>setScoreHelp('stage')} style={helpButton}><QuestionCircleOutlined/></button></span>:x}</th>)}</tr></thead><tbody>{stageRows.map((stage,index)=>{
                    const top=Number(stage.top_perf??stage.top??0),bottom=Number(stage.bottom_perf??stage.bottom??top),clusters=d.design_data?.stage_cluster_design?.[index] as any,positions=(clusters?.cluster_positions_md??[]) as number[],clusterCount=positions.length||clusters?.cluster_per_stage||d.cluster_per_stage||0,displayPositions=positions.length?positions:Array.from({length:clusterCount},(_,i)=>top+(i+1)*(bottom-top)/(clusterCount+1)),spacing=clusters?.cluster_spacing??(displayPositions.length>1?(displayPositions[displayPositions.length-1]-displayPositions[0])/(displayPositions.length-1):0),check=score.checks[index];
                    return <tr key={stage.id??index}><td style={stageTd}><b>{stage.stage_number??index+1}</b></td><td style={stageTd}>{top.toFixed(1)}</td><td style={stageTd}>{bottom.toFixed(1)}</td><td style={stageTd}>{(bottom-top).toFixed(1)}</td><td style={stageTd}>{clusterCount}</td><td style={stageTd}>{Number(spacing).toFixed(1)} m</td><td style={stageTd}>{check?<span style={{padding:'2px 6px',borderRadius:3,background:check.bg,color:check.color,fontWeight:700}}>{check.label} {check.score}</span>:'—'}</td><td style={{...stageTd,width:'32%'}}><div style={{height:43,position:'relative',border:`1px solid ${TH.headerBorder}`,background:'#F8FAFC',minWidth:240,overflow:'hidden'}}><span style={{position:'absolute',left:5,top:2,fontSize:8,color:TH.textMuted}}>{top.toFixed(1)} m</span><span style={{position:'absolute',right:5,top:2,fontSize:8,color:TH.textMuted}}>{bottom.toFixed(1)} m</span><span style={{position:'absolute',left:5,right:5,top:16,height:1,background:'#C8D2DC'}}/>{displayPositions.map((depth,i)=>{const left=Math.max(2,Math.min(98,(depth-top)/Math.max(1e-6,bottom-top)*100));return <React.Fragment key={i}><span title={`簇位 ${Number(depth).toFixed(1)} m`} style={{position:'absolute',left:`${left}%`,top:10,width:6,height:13,marginLeft:-3,borderRadius:3,background:positions.length?TH.stage:'#7C91A3'}}/><span style={{position:'absolute',left:`${left}%`,top:25,transform:'translateX(-50%)',fontSize:8,color:TH.textSec,whiteSpace:'nowrap'}}>{Number(depth).toFixed(1)}</span></React.Fragment>;})}</div></td></tr>;
                  })}</tbody></table></div>:<div style={{padding:40,textAlign:'center',color:TH.textMuted}}>该方案暂无分段明细</div>}
                </section>
              </div>
            </div>;
          })()}
        </main>
        </div>
      </>}
      <Modal
        open={scoreHelp !== null}
        title={scoreHelp === 'stage' ? '压裂段工程评分方程与参数' : '方案评分方程与参数'}
        onCancel={() => setScoreHelp(null)}
        footer={null}
        width={680}
        destroyOnClose
      >
        <div style={{ fontSize: 12, color: TH.textSec, lineHeight: 1.8 }}>
          {scoreHelp === 'stage' ? <>
            <div style={formulaBox}>段工程评分 = w₁ × 段长合理性 + w₂ × 簇数/簇间距 + w₃ × 地质品质匹配 + w₄ × 液砂匹配</div>
            <p><b style={{ color: TH.text }}>动态权重：</b>密切割强化簇参数与地质品质；长缝导向强化液砂规模；风险控制强化地质品质和段界；均衡设计保持综合权重。</p>
            <p><b style={{ color: TH.text }}>簇数/簇间距：</b>35% 簇数得分 + 45% 簇距得分 + 20% 簇群覆盖率得分；超出工程下限或覆盖率上限时执行风险限分。</p>
            <p><b style={{ color: TH.text }}>液砂匹配：</b>50% 单米液量得分 + 50% 单米砂量得分。</p>
            <p><b style={{ color: TH.text }}>区间得分：</b>参数位于推荐区间内为 100 分；超出区间后，按偏离推荐边界的相对距离线性扣分，最低为 0 分。</p>
          </> : <>
            <div style={formulaBox}>方案评分 = Σ（各压裂段工程评分）÷ 压裂段数</div>
            <p>每段先根据段内地质品质自动选择设计取向并完成评分，再计算所有压裂段的算术平均值。评分等级：82–100 为“推荐”，65–81 为“需提高”，低于 65 为“高风险”。</p>
          </>}
          <div style={{ margin: '12px 0 7px', fontWeight: 700, color: TH.text }}>当前评分参考</div>
          <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', border: `1px solid ${TH.headerBorder}` }}>
            {[
              ['储层类型', RESERVOIR_LABELS[activeScoringTemplate.reservoirType]],
              ['主导推荐段长', `${activeScoringTemplate.stageLength.min}–${activeScoringTemplate.stageLength.max} m`],
              ['主导推荐簇数', `${activeScoringTemplate.clusters.min}–${activeScoringTemplate.clusters.max} 簇/段`],
              ['主导推荐簇间距', `${activeScoringTemplate.clusterSpacing.min}–${activeScoringTemplate.clusterSpacing.max} m`],
              ['主导单米液量', `${activeScoringTemplate.fluidIntensity.min}–${activeScoringTemplate.fluidIntensity.max} m³/m`],
              ['主导单米砂量', `${activeScoringTemplate.proppantIntensity.min}–${activeScoringTemplate.proppantIntensity.max} t/m`],
              ['簇群最大覆盖率', `${Math.round(activeScoringTemplate.maxClusterCoverageRatio * 100)}%`],
            ].map(([label, value]) => <React.Fragment key={label}><div style={formulaLabel}>{label}</div><div style={formulaValue}>{value}</div></React.Fragment>)}
          </div>
          <div style={{ marginTop: 10, padding: '9px 11px', borderLeft: `3px solid ${selectedPlanScore?.color||TH.warn}`, background: selectedPlanScore?.bg||'#FFF8ED', color: selectedPlanScore?.color||TH.textSec, fontWeight: 700 }}>{scoreGuidance(selectedPlanScore)}</div>
        </div>
      </Modal>
      <Modal
        open={Boolean(renameTarget)}
        title="重命名方案"
        okText="保存"
        cancelText="取消"
        confirmLoading={renameBusy}
        okButtonProps={{ disabled: !renameName.trim() || renameName.trim() === renameTarget?.name }}
        onOk={submitRename}
        onCancel={() => !renameBusy && setRenameTarget(null)}
        destroyOnClose
      >
        <label style={{ display: 'block', fontSize: 12, color: TH.textSec, marginBottom: 6 }}>方案名称</label>
        <input
          autoFocus
          value={renameName}
          maxLength={100}
          onChange={(event) => setRenameName(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && renameName.trim() && renameName.trim() !== renameTarget?.name) submitRename();
          }}
          style={{ width: '100%', height: 34, padding: '0 10px', border: `1px solid ${TH.headerBorder}`, borderRadius: 4, color: TH.text, outline: 'none' }}
        />
        <div style={{ marginTop: 6, textAlign: 'right', fontSize: 11, color: TH.textMuted }}>{renameName.length}/100</div>
      </Modal>

      <Modal
        open={importOpen}
        title="人工导入 Excel 方案"
        onCancel={() => setImportOpen(false)}
        footer={null}
        destroyOnClose
      >
        <Upload.Dragger
          accept=".xlsx,.xls,.csv"
          showUploadList={false}
          beforeUpload={(f) => handleImport(f as unknown as File)}
        >
          <p className="ant-upload-drag-icon"><FileExcelOutlined /></p>
          <p className="ant-upload-text">点击或拖拽 Excel 文件到此</p>
          <p className="ant-upload-hint" style={{ fontSize: 11, color: TH.textMuted }}>
            支持 .xlsx / .xls / .csv；自动识别中文、英文及 Petrel 常见字段，工作表名称和列顺序不限。
          </p>
        </Upload.Dragger>
        <div style={{ marginTop: 12, padding: '10px 12px', border: `1px solid ${TH.headerBorder}`, borderRadius: 5, background: '#F7FAFC', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
          <b style={{ display: 'block', fontSize: 12, color: TH.text }}>压裂方案通用 Excel 模板</b>
          <button type="button" onClick={downloadFracturePlanTemplate} style={{ ...btnSecondary, flexShrink: 0 }}><DownloadOutlined /> 下载模板</button>
        </div>
        <div style={{ marginTop: 14, padding: '12px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, border: `1px solid ${TH.headerBorder}`, borderRadius: 5, background: '#F8FAFC' }}>
          <b style={{ display: 'block', fontSize: 12, color: TH.text }}>没有可导入的人工设计方案？</b>
          <button type="button" onClick={() => { setImportOpen(false); setNewDesignName('新建方案'); setNewDesignOpen(true); }} style={{ ...btnPrimary, flexShrink: 0 }}>新建</button>
        </div>
      </Modal>

      <Modal
        open={newDesignOpen}
        title="新建人工设计方案"
        okText="创建并进入工程设计"
        cancelText="取消"
        confirmLoading={newDesignBusy}
        okButtonProps={{ disabled: !newDesignName.trim() }}
        onOk={createManualDesign}
        onCancel={() => !newDesignBusy && setNewDesignOpen(false)}
        destroyOnClose
      >
        <label style={{ display: 'block', marginBottom: 7, fontSize: 12, fontWeight: 700, color: TH.text }}>方案名称</label>
        <input autoFocus maxLength={100} value={newDesignName} onChange={event => setNewDesignName(event.target.value)} onKeyDown={event => { if (event.key === 'Enter' && newDesignName.trim()) void createManualDesign(); }} style={{ width: '100%', height: 36, padding: '0 10px', border: `1px solid ${TH.headerBorder}`, borderRadius: 4, outline: 'none', color: TH.text }}/>
        <div style={{ marginTop: 8, fontSize: 11, color: TH.textMuted }}>若名称已经存在，系统将在名称末尾自动增加“（1）”“（2）”“（3）”等序号。</div>
      </Modal>

      {/* AI 生成方案 —— 只收"补充要求", 不让用户直接填数值 */}
      <Modal
        open={aiOpen}
        title="AI 生成压裂方案"
        onCancel={() => setAiOpen(false)}
        onOk={aiDesign}
        okText={busy ? '生成中…' : '开始生成'}
        confirmLoading={busy}
        destroyOnClose
      >
        <div style={{ fontSize: 12, color: TH.textSec, lineHeight: 1.8, marginBottom: 12 }}>
          大模型读取本项目的地层、甜点分级、岩石力学与避让区,决定<b>储层类型、设计取向和
          各项参数区间</b>;逐段的液量、砂量、排量、泵序仍由平台的模板引擎按这些区间计算,
          并受该储层类型的 guardrails 约束。模型给的区间越界会被夹回,生成后会逐条列出。
        </div>
        <Form layout="vertical">
          <Form.Item label="补充要求(选填)" style={{ marginBottom: 0 }}>
            <Input.TextArea
              rows={3}
              value={brief}
              onChange={(e) => setBrief(e.target.value)}
              placeholder="例:这口井套变风险高,优先控制缝高;或:成本优先,砂量压到 3t/m 以内"
              maxLength={500}
              showCount
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 生成结果的留痕 —— 甲方问"这个数哪来的", 答案在这里 */}
      <Modal
        open={!!aiAudit}
        title="AI设计"
        onCancel={() => setAiAudit(null)}
        footer={null}
        width={620}
      >
        {aiAudit && (
          <div style={{ fontSize: 12, color: TH.textSec, lineHeight: 1.9 }}>
            {aiAudit.source !== 'llm' && aiAudit.error && (
              <div style={{ background: '#FFF7E6', padding: '8px 10px', borderRadius: 4, marginBottom: 10 }}>
                <div><b style={{ color: TH.text }}>为什么没用上大模型:</b> {aiAudit.error}</div>
                {aiAudit.error_hint && <div style={{ fontSize: 11, color: TH.textMuted }}>{aiAudit.error_hint}</div>}
              </div>
            )}
            {aiAudit.rationale && (
              <div style={{ marginBottom: 10 }}><b style={{ color: TH.text }}>设计依据:</b> {aiAudit.rationale}</div>
            )}
            {!!aiAudit.risks?.length && (
              <div style={{ marginBottom: 10 }}>
                <b style={{ color: TH.text }}>风险提示:</b>
                <ul style={{ margin: '4px 0 0 18px', padding: 0 }}>
                  {aiAudit.risks.map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal
        open={analogOpen}
        title="邻井择选"
        okText="生成择选方案"
        cancelText="取消"
        okButtonProps={{ disabled: !selectedAnalogId || analogLoading, loading: busy }}
        onOk={runAnalogDesign}
        onCancel={() => setAnalogOpen(false)}
        destroyOnClose
        width={620}
      >
        <div style={{ border: `1px solid ${TH.headerBorder}`, maxHeight: 320, overflow: 'auto' }}>
          {analogLoading ? (
            <div style={{ padding: 28, textAlign: 'center', color: TH.textMuted }}>读取中…</div>
          ) : analogCandidates.length ? (
            analogCandidates.map((candidate) => (
              <label key={candidate.design_id} style={{ display: 'grid', gridTemplateColumns: '28px 1fr 100px 76px 70px', alignItems: 'center', gap: 8, padding: '9px 12px', borderBottom: `1px solid ${TH.headerBorder}`, cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="analog-candidate"
                  checked={selectedAnalogId === candidate.design_id}
                  onChange={() => setSelectedAnalogId(candidate.design_id)}
                />
                <span style={{ minWidth: 0 }}>
                  <b style={{ color: TH.text }}>{candidate.well_name}</b>
                  <span style={{ color: TH.textMuted, marginLeft: 8 }}>{candidate.design_name}</span>
                </span>
                <span style={{ color: TH.textSec }}>{candidate.target_formation || '未标注层位'}</span>
                <b style={{ color: candidate.similarity_score>=80?'#177D63':candidate.similarity_score>=65?'#A46117':'#B23A32', textAlign:'right' }}>{candidate.similarity_score.toFixed(1)}分</b>
                <span style={{ color: TH.textSec, textAlign: 'right' }}>{candidate.stage_count} 段</span>
              </label>
            ))
          ) : (
            <div style={{ padding: 28, textAlign: 'center', color: TH.textMuted }}>暂无其他井且包含段表数据的压裂方案</div>
          )}
        </div>
      </Modal>

      <Modal
        open={compareOpen}
        title="方案对比"
        footer={null}
        onCancel={() => setCompareOpen(false)}
        width={900}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '250px 1fr', gap: 12, minHeight: 300 }}>
          <div style={{ border: `1px solid ${TH.headerBorder}`, maxHeight: 420, overflow: 'auto' }}>
            {visibleDesigns.map((item) => (
              <label key={item.id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '8px 10px', borderBottom: `1px solid ${TH.headerBorder}`, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={compareIds.includes(item.id)}
                  onChange={(event) => toggleComparison(item.id, event.target.checked)}
                />
                <span style={{ minWidth: 0 }}>
                  <b style={{ display: 'block', color: TH.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</b>
                  <span style={{ fontSize: 11, color: TH.textMuted }}>{item.stage_count} 段 · {item.cluster_per_stage} 簇/段</span>
                </span>
              </label>
            ))}
          </div>
          <div style={{ overflow: 'auto' }}>
            {comparedDesigns.length ? (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ background: TH.panelHeaderBg }}>
                    <th style={{ padding: 8, textAlign: 'left', border: `1px solid ${TH.headerBorder}` }}>指标</th>
                    {comparedDesigns.map((item) => <th key={item.id} style={{ padding: 8, textAlign: 'left', border: `1px solid ${TH.headerBorder}`, minWidth: 150 }}>{item.name}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['段数', (item: Design) => `${item.stage_count} 段`],
                    ['平均每段簇数', (item: Design) => `${item.cluster_per_stage} 簇`],
                    ['平均段长', (item: Design) => `${item.stage_spacing} m`],
                    ['平均簇距', (item: Design) => `${item.cluster_spacing} m`],
                    ['排量', (item: Design) => `${item.pump_rate} m³/min`],
                    ['总液量', (item: Design) => `${item.total_fluid} m³`],
                    ['总砂量', (item: Design) => `${item.total_proppant} t`],
                    ['风险/优化', (item: Design) => designInsight(item, scoreFracPlan(item, sweetEvals, scoringConfig))],
                  ].map(([label, getValue]) => (
                    <tr key={String(label)}>
                      <td style={{ padding: 8, color: TH.textMuted, border: `1px solid ${TH.headerBorder}` }}>{String(label)}</td>
                      {comparedDesigns.map((item) => <td key={item.id} style={{ padding: 8, color: TH.text, border: `1px solid ${TH.headerBorder}` }}>{(getValue as (value: Design) => string)(item)}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', color: TH.textMuted }}>请选择需要对比的方案</div>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
}

function TrajectoryStageThumbnail({ survey, layers, stages }: {
  survey: TrajectoryStation[];
  layers: FormationLayer[];
  stages: any[];
}) {
  const width = 300, height = 420;
  const margin = { left: 38, right: 16, top: 18, bottom: 27 };
  const stageTop = stages.length ? Math.min(...stages.map((row) => Number(row.top_perf ?? row.top ?? 0))) : 2800;
  const stageBottom = stages.length ? Math.max(...stages.map((row) => Number(row.bottom_perf ?? row.bottom ?? stageTop))) : 3800;
  const fallbackLanding = Math.max(600, stageTop * 0.9);
  const fallbackSurvey: TrajectoryStation[] = [
    { md: 0, tvd: 0, ns: 0, ew: 0 },
    { md: fallbackLanding * 0.72, tvd: fallbackLanding * 0.72, ns: 0, ew: 0 },
    { md: fallbackLanding, tvd: fallbackLanding * 0.94, ns: 70, ew: 0 },
    { md: Math.max(stageBottom, fallbackLanding + 800), tvd: fallbackLanding, ns: Math.max(850, stageBottom - fallbackLanding), ew: 0 },
  ];
  const stations = (survey.length > 1 ? survey : fallbackSurvey).filter((row) => Number.isFinite(row.md) && Number.isFinite(row.tvd)).sort((a, b) => a.md - b.md);
  const projected = stations.map((station, index) => {
    if (!index) return { ...station, horizontal: 0 };
    const previous = stations[index - 1];
    const dns = station.ns != null && previous.ns != null ? station.ns - previous.ns : null;
    const dew = station.ew != null && previous.ew != null ? station.ew - previous.ew : null;
    const displacement = dns != null && dew != null
      ? Math.hypot(dns, dew)
      : Math.sqrt(Math.max(0, (station.md - previous.md) ** 2 - (station.tvd - previous.tvd) ** 2));
    return { ...station, horizontal: displacement };
  });
  for (let index = 1; index < projected.length; index += 1) projected[index].horizontal += projected[index - 1].horizontal;
  if ((projected[projected.length - 1]?.horizontal ?? 0) < 1) {
    projected.forEach((station) => { station.horizontal = Math.max(0, station.md - station.tvd); });
  }
  const minTvd = Math.min(0, ...projected.map((row) => row.tvd));
  const maxTvd = Math.max(1, ...projected.map((row) => row.tvd));
  const maxHorizontal = Math.max(1, ...projected.map((row) => row.horizontal));
  const xScale = (value: number) => margin.left + value / maxHorizontal * (width - margin.left - margin.right);
  const yScale = (value: number) => margin.top + (value - minTvd) / Math.max(1, maxTvd - minTvd) * (height - margin.top - margin.bottom);
  const pointAt = (md: number) => {
    let index = projected.findIndex((row) => row.md >= md);
    if (index < 0) index = projected.length - 1;
    if (index === 0) return { x: xScale(projected[0].horizontal), y: yScale(projected[0].tvd) };
    const a = projected[index - 1], b = projected[index];
    const ratio = Math.max(0, Math.min(1, (md - a.md) / Math.max(1e-6, b.md - a.md)));
    return {
      x: xScale(a.horizontal + (b.horizontal - a.horizontal) * ratio),
      y: yScale(a.tvd + (b.tvd - a.tvd) * ratio),
    };
  };
  const tvdYAt = (md: number) => pointAt(md).y;
  const trajectoryPoints = projected.map((row) => `${xScale(row.horizontal).toFixed(1)},${yScale(row.tvd).toFixed(1)}`).join(' ');
  const depthTicks = Array.from({ length: 6 }, (_, index) => minTvd + (maxTvd - minTvd) * index / 5);

  return <div style={{ flex: 1, minHeight: 420, background: '#FBFCFD', position: 'relative', overflow: 'hidden' }}>
    <svg role="img" aria-label="井轨迹与段簇缩略图" viewBox={`0 0 ${width} ${height}`} width="100%" height="100%" style={{ display: 'block', minHeight: 420 }}>
      <rect width={width} height={height} fill="#FBFCFD"/>
      {depthTicks.map((depth) => <g key={depth}>
        <line x1={margin.left} x2={width - margin.right} y1={yScale(depth)} y2={yScale(depth)} stroke="#E3E8ED" strokeWidth="0.8" strokeDasharray="2 3"/>
        <text x={margin.left - 5} y={yScale(depth) + 3} textAnchor="end" fontSize="8" fill={TH.textMuted} fontFamily={TH.fontMono}>{depth.toFixed(0)}</text>
      </g>)}
      <text x="8" y={height / 2} transform={`rotate(-90 8 ${height / 2})`} textAnchor="middle" fontSize="8" fill={TH.textMuted}>垂深 TVD (m)</text>
      {layers.map((layer, index) => {
        const topY = tvdYAt(layer.top), bottomY = tvdYAt(layer.bottom);
        const y = Math.min(topY, bottomY), bandHeight = Math.max(2, Math.abs(bottomY - topY));
        return <g key={`${layer.name}-${index}`}>
          <rect x={margin.left} y={y} width={width - margin.left - margin.right} height={bandHeight} fill={layer.color} fillOpacity="0.10"/>
          <line x1={margin.left} x2={width - margin.right} y1={y} y2={y} stroke={layer.color} strokeOpacity="0.72" strokeWidth="1"/>
          <text x={margin.left + 4} y={Math.min(height - margin.bottom - 2, y + Math.max(9, bandHeight / 2))} fontSize="8" fontWeight="600" fill="#394B5D">{layer.name}</text>
        </g>;
      })}
      <polyline points={trajectoryPoints} fill="none" stroke="#4B5967" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" opacity="0.22"/>
      <polyline points={trajectoryPoints} fill="none" stroke="#8B3A2A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      {stages.map((stage, index) => {
        const top = Number(stage.top_perf ?? stage.top ?? 0), bottom = Number(stage.bottom_perf ?? stage.bottom ?? top);
        const mdSamples = [top, ...projected.filter((row) => row.md > top && row.md < bottom).map((row) => row.md), bottom];
        const points = mdSamples.map((md) => { const point = pointAt(md); return `${point.x.toFixed(1)},${point.y.toFixed(1)}`; }).join(' ');
        const middle = pointAt((top + bottom) / 2);
        return <g key={stage.id ?? index}>
          <polyline points={points} fill="none" stroke={index % 2 ? '#2874A6' : '#1E8449'} strokeWidth="6" strokeLinecap="butt" opacity="0.82"/>
          <text x={middle.x} y={middle.y - 7} textAnchor="middle" fontSize="7.5" fontWeight="700" fill="#164E70">{stage.stage_number ?? index + 1}</text>
        </g>;
      })}
      <g transform={`translate(${width - 100} 10)`}>
        <rect width="88" height="38" rx="3" fill="#FFFFFF" fillOpacity="0.92" stroke="#D6DAE0"/>
        <line x1="8" x2="25" y1="12" y2="12" stroke="#8B3A2A" strokeWidth="2"/><text x="31" y="15" fontSize="7.5" fill={TH.textSec}>井轨迹</text>
        <line x1="8" x2="25" y1="27" y2="27" stroke="#2874A6" strokeWidth="5"/><text x="31" y="30" fontSize="7.5" fill={TH.textSec}>压裂段</text>
      </g>
      <text x={width - margin.right} y={height - 8} textAnchor="end" fontSize="8" fill={TH.textMuted}>水平位移剖面</text>
    </svg>
    {!survey.length && <span style={{ position: 'absolute', left: 45, top: 8, fontSize: 9, color: TH.warn, background: '#FFF8ED', padding: '2px 5px', borderRadius: 3 }}>暂无测斜数据，当前为示意轨迹</span>}
  </div>;
}

const stageTd:React.CSSProperties={padding:'5px 8px',borderBottom:'1px solid #F0EDE4',fontFamily:TH.fontMono,whiteSpace:'nowrap'};
const btnPrimary: React.CSSProperties = {
  padding: '5px 14px', borderRadius: 4, fontSize: 12, fontWeight: 600,
  background: TH.accent, color: '#fff', border: 'none', cursor: 'pointer',
};
const btnSecondary: React.CSSProperties = {
  padding: '5px 14px', borderRadius: 4, fontSize: 12, fontWeight: 600,
  background: '#fff', color: TH.stage, border: `1px solid ${TH.stage}`, cursor: 'pointer',
};
const btnCard: React.CSSProperties = {
  padding: '3px 10px', fontSize: 11, borderRadius: 3,
  background: '#fff', border: `1px solid ${TH.headerBorder}`, cursor: 'pointer', color: TH.textSec,
};
const btnCardPrimary: React.CSSProperties = {
  ...btnCard, background: TH.accent, color: '#fff', border: 'none', fontWeight: 600,
};
const thAudit: React.CSSProperties = {
  textAlign: 'left', padding: '4px 8px', borderBottom: `1px solid ${TH.headerBorder}`, fontWeight: 600,
};
const tdAudit: React.CSSProperties = {
  padding: '4px 8px', borderBottom: `1px solid ${TH.headerBorder}`, verticalAlign: 'top',
};
const createPanel: React.CSSProperties = {
  minHeight: 600, overflow: 'hidden', border: `1px solid ${TH.headerBorder}`, borderRadius: 7,
  background: '#fff', boxShadow: '0 3px 12px rgba(35,52,70,0.07)',
};
const createPanelHeader: React.CSSProperties = {
  height: 46, padding: '0 15px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  borderBottom: `1px solid ${TH.headerBorder}`, borderLeft: `4px solid ${TH.stage}`, background: '#F7F9FB', color: TH.text,
  fontFamily: 'inherit', fontSize: 14, fontWeight: 700,
};
const creationCard: React.CSSProperties = {
  minHeight: 150, padding: '20px 22px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center', gap: 9,
  border: `1px solid #CCD6E0`, borderRadius: 6, background: '#FBFCFD', color: TH.text, cursor: 'pointer', textAlign: 'left',
  boxShadow: '0 2px 7px rgba(35,52,70,0.05)',
};
const designEntry: React.CSSProperties = {
  width: '100%', minHeight: 46, padding: '8px 14px', display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 170px 80px 120px',
  alignItems: 'center', gap: 12, border: 'none', borderBottom: `1px solid ${TH.headerBorder}`, background: '#fff', color: TH.text,
  cursor: 'pointer', fontSize: 12, textAlign: 'left',
};
const helpButton: React.CSSProperties = {
  width: 18, height: 18, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  border: 'none', borderRadius: '50%', background: 'transparent', color: TH.stage, cursor: 'pointer', fontSize: 12,
};
const formulaBox: React.CSSProperties = {
  padding: '10px 12px', border: `1px solid ${TH.headerBorder}`, borderLeft: `3px solid ${TH.stage}`,
  background: '#F7FAFC', color: TH.text, fontFamily: TH.fontMono, fontWeight: 700,
};
const formulaLabel: React.CSSProperties = {
  padding: '6px 9px', background: TH.panelHeaderBg, borderBottom: `1px solid ${TH.headerBorder}`, color: TH.textSec,
};
const formulaValue: React.CSSProperties = {
  padding: '6px 9px', borderBottom: `1px solid ${TH.headerBorder}`, color: TH.text, fontFamily: TH.fontMono,
};
